"use client";

import { useState } from "react";
import Link from "next/link";

import { AnimatePresence, motion, type Variants } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { WHATSAPP_LINK } from "@/lib/constants";

const navVariants: Variants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

const mobileMenuVariants: Variants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

export function SiteNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const navLinks = [
    { label: "Paket", href: "/#pricing" },
    { label: "Alur", href: "/#workflow" },
    { label: "Kontak", href: "/#contact" },
    { label: "Portofolio", href: "/portfolio" },
  ];

  return (
    <nav className="sticky top-4 z-50 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
      <motion.div
        className="relative rounded-3xl border border-white/20 bg-[#04122b]/85 px-5 py-3 text-white shadow-lg backdrop-blur-2xl"
        initial="hidden"
        animate="visible"
        variants={navVariants}
      >
        <div className="flex items-center justify-between gap-3">
          <Link href="/" className="text-lg font-semibold text-white hover:text-[#F2F7A1]" onClick={closeMenu}>
            WallSite
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-6 text-sm text-white/80 sm:flex">
              {navLinks.map((link) => (
                <Link key={link.label} href={link.href} className="hover:text-[#F2F7A1]">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="hidden sm:flex">
              <Button
                asChild
                size="sm"
                className="rounded-full border border-white/30 bg-white/10 text-white hover:bg-white/20"
              >
                <Link href={WHATSAPP_LINK}>Konsultasi Cepat</Link>
              </Button>
            </div>
            <div className="sm:hidden flex items-center gap-2">
              <Button
                asChild
                size="icon"
                className="rounded-full bg-white/20 text-white hover:bg-white/30"
              >
                <Link href={WHATSAPP_LINK} aria-label="WhatsApp">
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="rounded-full bg-white/10 text-white hover:bg-white/20"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label="Toggle menu"
              >
                {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="absolute left-0 right-0 top-full mt-3 flex flex-col gap-4 rounded-2xl border border-white/15 bg-[#04122b]/95 p-4 text-sm text-white shadow-[0_25px_70px_-40px_rgba(4,18,43,0.9)] sm:hidden"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={mobileMenuVariants}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="rounded-xl bg-white/5 px-4 py-3 font-medium text-white hover:bg-white/15"
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
              <Button
                asChild
                className="w-full rounded-2xl border border-white/30 bg-white/20 text-white hover:bg-white/30"
                onClick={closeMenu}
              >
                <Link href={WHATSAPP_LINK}>Konsultasi Cepat</Link>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </nav>
  );
}
